import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import SearchableSelect from "../../components/common/SearchableSelect";
import { confirmAlert, errorAlert, successAlert, warningAlert } from "../../utils/alert";
import {
  FaArrowLeft,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaExclamationTriangle,
  FaInfoCircle,
  FaLayerGroup,
  FaPlus,
  FaRunning,
  FaSave,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUndo,
} from "react-icons/fa";

const AGE_OPTIONS = [
  { value: "all", label: "All Age Categories" },
  { value: "sub_junior", label: "Sub-Junior" },
  { value: "junior", label: "Junior" },
  { value: "senior", label: "Senior" },
];

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const normalizeGender = (value) => {
  const v = String(value || "").toLowerCase().trim();
  if (v === "male" || v === "men") return "male";
  if (v === "female" || v === "women") return "female";
  return "";
};

const normalizeAgeCategory = (value) => {
  const v = String(value || "").toLowerCase().trim();
  if (v === "all") return "all";
  if (v === "sub_junior" || v === "sub-junior" || v === "sub junior") return "sub_junior";
  if (v === "junior") return "junior";
  if (v === "senior") return "senior";
  return "";
};

const splitClasses = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const naturalCompare = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, { numeric: true });

/* =========================================================================
   ClassificationMultiSelect Component
   ========================================================================= */
// Kept for compatibility with older saved screens while class rules are normalized.
// eslint-disable-next-line no-unused-vars
const ClassificationMultiSelect = ({ options = [], value, onChange, disabled = false }) => {
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const selected = splitClasses(value);
  const available = Array.from(new Set(options.map((item) => String(item || "").trim()).filter(Boolean)));
  const displayOptions = Array.from(new Set([...available, ...selected]));
  const selectedSet = new Set(selected);
  const allSelected = available.length > 0 && available.every((item) => selectedSet.has(item));

  const toggle = (classification) => {
    const next = selectedSet.has(classification)
      ? selected.filter((item) => item !== classification)
      : [...selected, classification];
    onChange(next.join(","));
  };

  useEffect(() => {
    if (!open) return undefined;
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      const menuHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const opensAbove = spaceBelow < menuHeight && rect.top > spaceBelow;
      setMenuStyle({
        position: "fixed",
        left: rect.left,
        top: opensAbove ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4,
        width: Math.max(300, rect.width),
        maxWidth: "90vw",
        zIndex: 99999,
      });
    }

    const closeOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const closeOnScroll = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    window.addEventListener("scroll", closeOnScroll, true);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      window.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={`w-full min-h-[36px] border border-gray-300 rounded-lg px-3 py-1.5 cursor-pointer bg-white flex items-center justify-between gap-2 text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          disabled ? "opacity-60 cursor-not-allowed bg-gray-100" : "hover:border-gray-400"
        }`}
        onClick={() => !disabled && setOpen((current) => !current)}
        disabled={disabled}
      >
        <span className="truncate" title={selected.join(", ")}>
          {disabled
            ? "Select a sport first"
            : selected.length
            ? selected.join(", ")
            : "Select eligible classes"}
        </span>
        <span aria-hidden="true" className="text-gray-400 text-[10px]">
          ▼
        </span>
      </button>

      {!disabled &&
        open &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="rounded-xl border border-gray-200 bg-white shadow-2xl p-2.5 z-[99999]"
          >
            <div className="flex items-center justify-between gap-2 pb-2 mb-2 border-b border-gray-100">
              <button
                type="button"
                onClick={() => onChange(allSelected ? "" : available.join(","))}
                disabled={!available.length}
                className="text-xs font-semibold text-blue-700 hover:text-blue-800 disabled:text-gray-400"
              >
                {allSelected ? "Clear All" : `Select All (${available.length})`}
              </button>
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange("")}
                  className="text-xs text-gray-500 hover:text-rose-600"
                >
                  Clear Selection
                </button>
              )}
            </div>
            <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
              {!displayOptions.length ? (
                <p className="p-2 text-xs text-amber-700">No classifications found for this sport.</p>
              ) : (
                displayOptions.map((classification) => (
                  <label
                    key={classification}
                    className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer transition ${
                      selectedSet.has(classification)
                        ? "bg-blue-50 text-blue-900 font-medium"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSet.has(classification)}
                      onChange={() => toggle(classification)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span>{classification}</span>
                    {!available.includes(classification) && (
                      <span className="ml-auto text-[10px] text-amber-700 font-normal">Custom</span>
                    )}
                  </label>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

/* =========================================================================
   Main EligibilityManager Component
   ========================================================================= */
const EligibilityManager = () => {
  const navigate = useNavigate();

  // State: grouped rule records
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState("database");
  const [isDirty, setIsDirty] = useState(false);

  // Filters
  const [query, setQuery] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [sportFilter, setSportFilter] = useState("all");
  const [showOnlyInvalid, setShowOnlyInvalid] = useState(false);

  // Expanded rows state
  const [expandedGroupIds, setExpandedGroupIds] = useState(new Set());

  // Master options
  const [sports, setSports] = useState([]);
  const [classificationsBySport, setClassificationsBySport] = useState({});
  const [gamesBySport, setGamesBySport] = useState({});

  // Class Modal state (for Adding or Editing an individual Class)
  const [classModal, setClassModal] = useState({
    isOpen: false,
    mode: "add", // "add" | "edit"
    groupId: "",
    classId: null,
    competitionClass: "",
    mqs: "",
    equipment: "-",
  });

  // Event Modal state (for Adding a New Event Rule)
  const [eventModal, setEventModal] = useState({
    isOpen: false,
    gender: "male",
    age_category: "all",
    sport_id: "",
    game_id: "",
    initial_class: "",
    initial_mqs: "",
  });

  /* -------------------------------------------------------------------------
     Helper: Group flat backend rules into (gender + age + sport + game) groups
     ------------------------------------------------------------------------- */
  const groupFlatRules = (flatList, sportsList = []) => {
    const map = new Map();

    (Array.isArray(flatList) ? flatList : []).forEach((r, i) => {
      const gender = normalizeGender(r?.gender) || "male";
      const ageCategory = normalizeAgeCategory(r?.age_category) || "all";
      const sportId = String(r?.sport_id || "");
      const gameId = String(r?.game_id || "");
      const groupKey = `${gender}__${ageCategory}__${sportId}__${gameId}`;

      if (!map.has(groupKey)) {
        map.set(groupKey, {
          id: `grp_${i}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          gender,
          age_category: ageCategory,
          sport_id: sportId,
          game_id: gameId,
          sport: String(r?.sport || ""),
          event: String(r?.event || ""),
          classes: [],
        });
      }

      const legacyClass = String(r?.competition_class || "").trim().toUpperCase();
      const rowClasses = splitClasses(r?.eligible_classes || legacyClass);

      (rowClasses.length ? rowClasses : [legacyClass]).filter(Boolean).forEach((className, classIndex) => {
        const normalizedClass = String(className).trim().toUpperCase();
        const group = map.get(groupKey);
        const existing = group.classes.find(
          (item) => item.competition_class.toUpperCase() === normalizedClass
        );
        if (existing) return;
        group.classes.push({
          id: `cls_${r?.id ?? i}_${classIndex}_${Math.random().toString(36).slice(2, 7)}`,
          competition_class: normalizedClass,
          eligible_classes: normalizedClass,
          mqs: String(r?.mqs || "").trim(),
          equipment: String(r?.equipment || "-").trim() || "-",
        });
      });
    });

    const result = Array.from(map.values());
    result.forEach((group) => {
      group.classes.sort((a, b) => naturalCompare(a.competition_class, b.competition_class));
    });

    return result;
  };

  /* -------------------------------------------------------------------------
     Load Rules from API
     ------------------------------------------------------------------------- */
  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await API.fetchEligibilityRules();
      const list = Array.isArray(res?.data) ? res.data : [];
      const grouped = groupFlatRules(list);
      setGroups(grouped);
      setSource(String(res?.source || "database"));
      setIsDirty(false);
    } catch (err) {
      errorAlert("Load failed", err?.message || "Unable to load eligibility rules.");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------------------------------------------------------------
     Load Sports, Classifications, and Games
     ------------------------------------------------------------------------- */
  useEffect(() => {
    const loadEligibilityOptions = async () => {
      try {
        const sportRows = await API.getSports();
        const list = (Array.isArray(sportRows) ? sportRows : sportRows?.data || [])
          .filter((sport) => sport?.id && String(sport?.name || "").trim())
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setSports(list);

        const results = await Promise.all(
          list.map(async (sport) => {
            const [classificationResult, gamesResult] = await Promise.allSettled([
              API.get(`/admin/classifications/sports/${sport.id}/classifications`),
              API.fetchGamesBySport(sport.id),
            ]);
            const response =
              classificationResult.status === "fulfilled" ? classificationResult.value : [];
            const rows = Array.isArray(response) ? response : response?.data || [];
            const games =
              gamesResult.status === "fulfilled" && Array.isArray(gamesResult.value)
                ? gamesResult.value
                : [];
            return {
              id: String(sport.id),
              classifications: Array.from(
                new Set(rows.map((row) => String(row?.classification || "").trim()).filter(Boolean))
              ).sort((a, b) => naturalCompare(a, b)),
              games: games
                .filter((game) => game?.id && String(game?.game_name || "").trim())
                .sort((a, b) => String(a.game_name).localeCompare(String(b.game_name))),
            };
          })
        );
        setClassificationsBySport(
          Object.fromEntries(results.map((result) => [result.id, result.classifications]))
        );
        setGamesBySport(Object.fromEntries(results.map((result) => [result.id, result.games])));
      } catch (err) {
        errorAlert("Options unavailable", err?.message || "Unable to load sports and classifications.");
        setSports([]);
        setClassificationsBySport({});
        setGamesBySport({});
      }
    };

    loadEligibilityOptions();
  }, []);

  /* -------------------------------------------------------------------------
     Helpers for Master Options
     ------------------------------------------------------------------------- */
  const sportNameForId = (sportId) =>
    String(sports.find((sport) => String(sport.id) === String(sportId))?.name || "");

  const classificationOptionsForSport = (sportId) => {
    const base = sportId ? classificationsBySport[String(sportId)] || [] : [];
    const sportName = sportNameForId(sportId);
    const normalizedSport = String(sportName || "").trim().toLowerCase();
    if (normalizedSport === "para athletics") {
      return Array.from(
        new Set([...base, ...base.filter((item) => /^T\d/i.test(item)).map((item) => `F${item.slice(1)}`)])
      ).sort((a, b) => naturalCompare(a, b));
    }
    if (normalizedSport === "para swimming") {
      const numbers = base.map((item) => String(item).match(/^S(\d+)$/i)?.[1]).filter(Boolean);
      return Array.from(
        new Set([...base, ...numbers.map((n) => `SB${n}`), ...numbers.map((n) => `SM${n}`)])
      ).sort((a, b) => naturalCompare(a, b));
    }
    return base;
  };

  /* -------------------------------------------------------------------------
     Row Validation
     ------------------------------------------------------------------------- */
  const validateGroup = (group) => {
    if (!normalizeGender(group.gender)) return true;
    if (!normalizeAgeCategory(group.age_category)) return true;
    if (!String(group.sport_id || "").trim()) return true;
    if (!String(group.game_id || "").trim()) return true;
    if (!Array.isArray(group.classes) || group.classes.length === 0) return true;
    const hasInvalidClass = group.classes.some(
      (c) => !String(c.eligible_classes || "").trim() || !String(c.mqs || "").trim()
    );
    return hasInvalidClass;
  };

  const groupsWithValidation = useMemo(() => {
    return groups.map((g, idx) => ({
      ...g,
      _idx: idx,
      _invalid: validateGroup(g),
    }));
  }, [groups]);

  /* -------------------------------------------------------------------------
     Filtering
     ------------------------------------------------------------------------- */
  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groupsWithValidation.filter((group) => {
      if (genderFilter !== "all" && normalizeGender(group.gender) !== genderFilter) return false;
      if (ageFilter !== "all" && normalizeAgeCategory(group.age_category) !== ageFilter) return false;
      if (sportFilter !== "all" && String(group.sport_id) !== String(sportFilter)) return false;
      if (showOnlyInvalid && !group._invalid) return false;
      if (!q) return true;

      const classesText = group.classes
        .map((c) => `${c.eligible_classes} ${c.mqs} ${c.equipment}`)
        .join(" ")
        .toLowerCase();
      const haystack = `${group.sport} ${group.event} ${group.gender} ${group.age_category} ${classesText}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [groupsWithValidation, query, genderFilter, ageFilter, sportFilter, showOnlyInvalid]);

  // Statistics
  const totalClassesCount = useMemo(
    () => groups.reduce((acc, g) => acc + (Array.isArray(g.classes) ? g.classes.length : 0), 0),
    [groups]
  );
  const invalidCount = useMemo(() => groupsWithValidation.filter((g) => g._invalid).length, [groupsWithValidation]);

  /* -------------------------------------------------------------------------
     Row Expand / Collapse
     ------------------------------------------------------------------------- */
  const toggleExpand = (groupId) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (expandedGroupIds.size > 0) {
      setExpandedGroupIds(new Set());
    } else {
      setExpandedGroupIds(new Set(groups.map((g) => g.id)));
    }
  };

  /* -------------------------------------------------------------------------
     Class Modal Handlers (Add / Edit Class)
     ------------------------------------------------------------------------- */
  const openAddClassModal = (groupId) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    setClassModal({
      isOpen: true,
      mode: "add",
      groupId,
      classId: null,
      competitionClass: "",
      mqs: "",
      equipment: "-",
    });
  };

  const openEditClassModal = (groupId, classItem) => {
    setClassModal({
      isOpen: true,
      mode: "edit",
      groupId,
      classId: classItem.id,
      competitionClass: classItem.eligible_classes || classItem.competition_class,
      mqs: classItem.mqs,
      equipment: classItem.equipment || "-",
    });
  };

  const closeClassModal = () => {
    setClassModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleSaveClassModal = () => {
    const compClass = String(classModal.competitionClass || "").trim().toUpperCase();
    const mqs = String(classModal.mqs || "").trim();
    const equipment = String(classModal.equipment || "-").trim() || "-";

    if (!compClass) {
      warningAlert("Missing Class", "Please select or enter a class.");
      return;
    }
    if (!mqs) {
      warningAlert("Missing MQS", "Please enter the Minimum Qualifying Standard (MQS).");
      return;
    }

    setGroups((prev) =>
      prev.map((group) => {
        if (group.id !== classModal.groupId) return group;

        const isDuplicate = group.classes.some(
          (c) =>
            c.id !== classModal.classId &&
            c.competition_class.toUpperCase() === compClass
        );
        if (isDuplicate) {
          warningAlert("Duplicate Class", `Class ${compClass} is already assigned to this event.`);
          return group;
        }

        let updatedClasses;
        if (classModal.mode === "edit") {
          updatedClasses = group.classes.map((c) =>
            c.id === classModal.classId
              ? {
                  ...c,
                  competition_class: compClass,
                  eligible_classes: compClass,
                  mqs,
                  equipment,
                }
              : c
          );
        } else {
          updatedClasses = [
            ...group.classes,
            {
              id: `cls_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              competition_class: compClass,
              eligible_classes: compClass,
              mqs,
              equipment,
            },
          ];
        }

        updatedClasses.sort((a, b) => naturalCompare(a.competition_class, b.competition_class));
        return { ...group, classes: updatedClasses };
      })
    );

    setIsDirty(true);
    closeClassModal();
    successAlert("Updated", `Class ${compClass} saved.`);
  };

  /* -------------------------------------------------------------------------
     Delete Class
     ------------------------------------------------------------------------- */
  const handleDeleteClass = async (groupId, classId, className) => {
    const confirm = await confirmAlert({
      title: "Delete Class?",
      text: `Remove class "${className}" from this event?`,
      confirmText: "Yes, delete class",
    });
    if (!confirm.isConfirmed) return;

    setGroups((prev) =>
      prev.map((group) => {
        if (group.id !== groupId) return group;
        return {
          ...group,
          classes: group.classes.filter((c) => c.id !== classId),
        };
      })
    );
    setIsDirty(true);
    successAlert("Deleted", `Class ${className} removed from event.`);
  };

  /* -------------------------------------------------------------------------
     Inline editing
     ------------------------------------------------------------------------- */
  const handleGroupChange = (groupId, field, value) => {
    setGroups((prev) =>
      prev.map((group) => {
        if (group.id !== groupId) return group;

        if (field === "sport_id") {
          const sportId = String(value || "");
          const sport = sportNameForId(sportId);
          const firstGame = (gamesBySport[sportId] || [])[0];
          return {
            ...group,
            sport_id: sportId,
            sport,
            game_id: firstGame?.id ? String(firstGame.id) : "",
            event: String(firstGame?.game_name || ""),
          };
        }

        if (field === "game_id") {
          const gameId = String(value || "");
          const game = (gamesBySport[String(group.sport_id)] || []).find(
            (item) => String(item.id) === gameId
          );
          return { ...group, game_id: gameId, event: String(game?.game_name || "") };
        }

        return { ...group, [field]: value };
      })
    );
    setIsDirty(true);
  };

  const handleInlineClassChange = (groupId, classId, field, value) => {
    setGroups((prev) =>
      prev.map((group) => {
        if (group.id !== groupId) return group;
        return {
          ...group,
          classes: group.classes.map((c) => {
            if (c.id !== classId) return c;
            if (field === "eligible_classes") {
              const className = String(value || "").toUpperCase();
              return {
                ...c,
                eligible_classes: className,
                competition_class: className,
              };
            }
            return { ...c, [field]: value };
          }),
        };
      })
    );
    setIsDirty(true);
  };

  /* -------------------------------------------------------------------------
     Delete Entire Event Group
     ------------------------------------------------------------------------- */
  const handleDeleteGroup = async (groupId) => {
    const group = groups.find((g) => g.id === groupId);
    const eventName = group?.event || "this event";
    const confirm = await confirmAlert({
      title: "Delete Event Rule?",
      text: `Are you sure you want to delete "${eventName}" (${group?.classes?.length || 0} classes)?`,
      confirmText: "Yes, delete event",
    });
    if (!confirm.isConfirmed) return;

    setGroups((prev) => prev.filter((g) => g.id !== groupId));
    setIsDirty(true);
    successAlert("Deleted", `Event rule for ${eventName} removed.`);
  };

  /* -------------------------------------------------------------------------
     Add New Event Rule Modal
     ------------------------------------------------------------------------- */
  const openAddEventModal = () => {
    const defaultSport = sports[0]?.id ? String(sports[0].id) : "";
    const defaultGames = defaultSport ? gamesBySport[defaultSport] || [] : [];
    const defaultGame = defaultGames[0]?.id ? String(defaultGames[0].id) : "";
    const defaultClass = defaultSport ? (classificationsBySport[defaultSport] || [])[0] || "" : "";

    setEventModal({
      isOpen: true,
      gender: "male",
      age_category: "all",
      sport_id: defaultSport,
      game_id: defaultGame,
      initial_class: defaultClass,
      initial_mqs: "",
    });
  };

  const closeAddEventModal = () => {
    setEventModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleCreateEventGroup = () => {
    if (!eventModal.sport_id) {
      warningAlert("Validation", "Please select a sport.");
      return;
    }
    if (!eventModal.game_id) {
      warningAlert("Validation", "Please select an event.");
      return;
    }

    const sportName = sportNameForId(eventModal.sport_id);
    const game = (gamesBySport[eventModal.sport_id] || []).find(
      (g) => String(g.id) === String(eventModal.game_id)
    );
    const eventName = String(game?.game_name || "");

    // Check if group already exists
    const exists = groups.some(
      (g) =>
        normalizeGender(g.gender) === normalizeGender(eventModal.gender) &&
        normalizeAgeCategory(g.age_category) === normalizeAgeCategory(eventModal.age_category) &&
        String(g.sport_id) === String(eventModal.sport_id) &&
        String(g.game_id) === String(eventModal.game_id)
    );
    if (exists) {
      warningAlert(
        "Already Exists",
        `An event rule already exists for ${sportName} - ${eventName} (${eventModal.gender}, ${eventModal.age_category}). You can add classes directly to it.`
      );
      return;
    }

    const initialClasses = [];
    if (eventModal.initial_class) {
      initialClasses.push({
        id: `cls_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        competition_class: eventModal.initial_class.toUpperCase(),
        eligible_classes: eventModal.initial_class.toUpperCase(),
        mqs: String(eventModal.initial_mqs || "00:00.00").trim(),
        equipment: "-",
      });
    }

    const newGroup = {
      id: `grp_new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      gender: eventModal.gender,
      age_category: eventModal.age_category,
      sport_id: String(eventModal.sport_id),
      game_id: String(eventModal.game_id),
      sport: sportName,
      event: eventName,
      classes: initialClasses,
    };

    setGroups((prev) => [newGroup, ...prev]);
    setExpandedGroupIds((prev) => new Set([...prev, newGroup.id]));
    setIsDirty(true);
    closeAddEventModal();
    successAlert("Event Rule Added", `Added ${eventName}. You can now configure its classes and MQS.`);
  };

  /* -------------------------------------------------------------------------
     Save All Changes to Backend
     ------------------------------------------------------------------------- */
  const saveRules = async () => {
    if (!groups.length) {
      warningAlert("No Rules", "Add at least one event rule before saving.");
      return;
    }

    // Flatten groups into the rules array expected by backend
    const normalized = [];
    for (let gIdx = 0; gIdx < groups.length; gIdx += 1) {
      const g = groups[gIdx];
      const gender = normalizeGender(g.gender);
      const ageCategory = normalizeAgeCategory(g.age_category);
      const sportId = Number(g.sport_id);
      const gameId = Number(g.game_id);

      if (!gender || !ageCategory || !sportId || !gameId) {
        warningAlert(
          "Validation Error",
          `Event row #${gIdx + 1} (${g.sport || "Unknown"} - ${g.event || "Unknown"}) is missing gender, age, sport, or event selection.`
        );
        return;
      }

      if (!Array.isArray(g.classes) || g.classes.length === 0) {
        warningAlert(
          "Empty Event Rule",
          `Event row #${gIdx + 1} (${g.sport} - ${g.event}) has no classes. Please add at least one class or delete the event row.`
        );
        return;
      }

      for (let cIdx = 0; cIdx < g.classes.length; cIdx += 1) {
        const c = g.classes[cIdx];
        const className = String(c.eligible_classes || c.competition_class || "").trim().toUpperCase();
        const mqs = String(c.mqs || "").trim();
        const equipment = String(c.equipment || "-").trim() || "-";

        if (!className || !mqs) {
          warningAlert(
            "Validation Error",
            `In event "${g.sport} - ${g.event}", class #${cIdx + 1} is missing a class name or MQS value.`
          );
          return;
        }

        normalized.push({
          gender,
          age_category: ageCategory,
          sport_id: sportId,
          game_id: gameId,
          eligible_classes: className,
          mqs,
          equipment,
        });
      }
    }

    setSaving(true);
    try {
      const res = await API.saveEligibilityRules(normalized);
      successAlert("Saved Successfully", res?.message || "All eligibility rules and MQS standards updated.");
      setIsDirty(false);
      await loadRules();
    } catch (err) {
      errorAlert("Save Failed", err?.message || "Unable to save eligibility rules to the database.");
    } finally {
      setSaving(false);
    }
  };

  /* -------------------------------------------------------------------------
     Reload from Config
     ------------------------------------------------------------------------- */
  const resetFromConfig = async () => {
    const confirm = await confirmAlert({
      title: "Reload From Config?",
      text: "This will overwrite database rules with defaults from config. Continue?",
      confirmText: "Yes, reload",
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await API.resetEligibilityRules();
      successAlert("Reloaded", res?.message || "Rules reloaded from config.");
      await loadRules();
    } catch (err) {
      errorAlert("Reset Failed", err?.message || "Unable to reload from config.");
    }
  };

  // Currently active group in Class Modal for context display
  const activeModalGroup = useMemo(
    () => groups.find((g) => g.id === classModal.groupId) || null,
    [groups, classModal.groupId]
  );

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50/60 pb-16">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          {/* =========================================================================
              Page Header & Actions
             ========================================================================= */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/admin")}
                  className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition shadow-sm"
                  title="Back to Admin Dashboard"
                >
                  <FaArrowLeft className="w-4 h-4" />
                </button>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                  Game Eligibility & MQS Management
                </h1>
              </div>
              <p className="text-sm text-slate-600 mt-1 pl-11">
                Configure event qualification criteria and Minimum Qualifying Standards (MQS) by sport, event, and class.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={resetFromConfig}
                disabled={loading || saving}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm font-semibold transition shadow-sm disabled:opacity-50"
              >
                <FaUndo className="w-3.5 h-3.5" />
                Reload From Config
              </button>

              <button
                type="button"
                onClick={openAddEventModal}
                disabled={loading || saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-semibold transition shadow-sm shadow-blue-500/20 disabled:opacity-50"
              >
                <FaPlus className="w-3.5 h-3.5" />
                Add Event Rule
              </button>

              <button
                type="button"
                onClick={saveRules}
                disabled={saving || loading}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs sm:text-sm font-bold text-white transition shadow-sm disabled:opacity-50 ${
                  isDirty
                    ? "bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-400 ring-offset-2 animate-pulse"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                <FaSave className="w-4 h-4" />
                {saving ? "Saving..." : isDirty ? "Save Changes (Unsaved)" : "Save Changes"}
              </button>
            </div>
          </div>

          {/* Unsaved changes notice banner */}
          {isDirty && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between text-amber-800 text-xs sm:text-sm shadow-sm animate-fade-in">
              <div className="flex items-center gap-2">
                <FaExclamationTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>
                  <strong>Unsaved changes detected:</strong> You have modified classes or event standards. Click{" "}
                  <strong>Save Changes</strong> above to persist your updates.
                </span>
              </div>
              <button
                type="button"
                onClick={saveRules}
                disabled={saving}
                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-xs transition shrink-0 ml-2"
              >
                Save Now
              </button>
            </div>
          )}

          {/* =========================================================================
              Summary Stats
             ========================================================================= */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5 mb-5">
            <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                <FaRunning className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Event Rows</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{groups.length}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                <FaLayerGroup className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Class Standards</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{totalClassesCount}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                <FaCheck className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Data Source</p>
                <p className="text-base font-bold text-slate-900 mt-0.5 capitalize">
                  {source === "database" ? "Database" : "Config Fallback"}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center gap-3.5">
              <div
                className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${
                  invalidCount > 0
                    ? "bg-rose-50 border-rose-100 text-rose-600"
                    : "bg-slate-50 border-slate-100 text-slate-400"
                }`}
              >
                <FaExclamationTriangle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Incomplete</p>
                <p className={`text-xl font-bold mt-0.5 ${invalidCount > 0 ? "text-rose-600" : "text-slate-900"}`}>
                  {invalidCount}
                </p>
              </div>
            </div>
          </div>

          {/* =========================================================================
              Filter & Search Controls
             ========================================================================= */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3.5 items-end">
              <div className="lg:col-span-4">
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Search Sport / Event / Class / MQS
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g. Athletics, 100m, T47, 00:14.50..."
                    className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                  <FaSearch className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-3 pointer-events-none" />
                </div>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Gender</label>
                <SearchableSelect
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  className="w-full text-xs sm:text-sm rounded-xl border border-slate-300 py-2 px-3"
                >
                  <option value="all">All Genders</option>
                  {GENDER_OPTIONS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Age Category</label>
                <SearchableSelect
                  value={ageFilter}
                  onChange={(e) => setAgeFilter(e.target.value)}
                  className="w-full text-xs sm:text-sm rounded-xl border border-slate-300 py-2 px-3"
                >
                  {AGE_OPTIONS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Sport</label>
                <SearchableSelect
                  value={sportFilter}
                  onChange={(e) => setSportFilter(e.target.value)}
                  className="w-full text-xs sm:text-sm rounded-xl border border-slate-300 py-2 px-3"
                >
                  <option value="all">All Sports</option>
                  {sports.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className="lg:col-span-2 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={toggleExpandAll}
                  className="w-full py-2 px-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold transition"
                >
                  {expandedGroupIds.size > 0 ? "Collapse All" : "Expand All"}
                </button>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlyInvalid}
                  onChange={(e) => setShowOnlyInvalid(e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span>Show only incomplete / invalid rows</span>
              </label>

              <div className="text-xs text-slate-500">
                Showing <strong>{filteredGroups.length}</strong> of <strong>{groups.length}</strong> event rules
              </div>
            </div>
          </div>

          {/* =========================================================================
              Grouped Event Table (Single Row per Event)
             ========================================================================= */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-xs sm:text-sm">
                <thead className="bg-slate-50 text-slate-700 font-semibold text-left">
                  <tr>
                    <th className="py-3 px-3 w-12 text-center">#</th>
                    <th className="py-3 px-3 w-28">Gender</th>
                    <th className="py-3 px-3 w-32">Age Category</th>
                    <th className="py-3 px-3 w-44">Sport</th>
                    <th className="py-3 px-3 w-48">Event</th>
                    <th className="py-3 px-3 min-w-[360px]">All Classes &amp; MQS Standards</th>
                    <th className="py-3 px-3 w-36 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-500">
                        <div className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
                          Loading eligibility rules...
                        </div>
                      </td>
                    </tr>
                  ) : !filteredGroups.length ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-500">
                        <p className="text-base font-semibold text-slate-700">No event rules found</p>
                        <p className="text-xs text-slate-400 mt-1">Try adjusting your search or filters.</p>
                      </td>
                    </tr>
                  ) : (
                    filteredGroups.map((group, idx) => {
                      const isExpanded = expandedGroupIds.has(group.id);
                      return (
                        <React.Fragment key={group.id}>
                          <tr
                            className={`transition hover:bg-slate-50/70 ${
                              group._invalid ? "bg-rose-50/50" : ""
                            }`}
                          >
                            {/* # Index */}
                            <td className="py-3.5 px-3 text-center text-slate-500 font-mono text-xs align-top">
                              {idx + 1}
                              {group._invalid && (
                                <span
                                  className="block text-rose-500 font-bold"
                                  title="Incomplete or missing required fields"
                                >
                                  ⚠️
                                </span>
                              )}
                            </td>

                            {/* Gender */}
                            <td className="py-3.5 px-3 align-top">
                              <SearchableSelect
                                value={group.gender}
                                onChange={(e) => handleGroupChange(group.id, "gender", e.target.value)}
                                className="w-full min-w-[105px] rounded-lg border border-slate-300 px-2 py-1.5 text-xs bg-white"
                              >
                                {GENDER_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </SearchableSelect>
                            </td>

                            {/* Age Category */}
                            <td className="py-3.5 px-3 align-top">
                              <SearchableSelect
                                value={group.age_category}
                                onChange={(e) => handleGroupChange(group.id, "age_category", e.target.value)}
                                className="w-full min-w-[125px] rounded-lg border border-slate-300 px-2 py-1.5 text-xs bg-white"
                              >
                                {AGE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </SearchableSelect>
                            </td>

                            {/* Sport */}
                            <td className="py-3.5 px-3 align-top font-semibold text-slate-800">
                                <SearchableSelect
                                  value={group.sport_id}
                                  onChange={(e) => handleGroupChange(group.id, "sport_id", e.target.value)}
                                  className="w-full min-w-[165px] rounded-lg border border-slate-300 px-2 py-1.5 text-xs bg-white font-normal"
                                >
                                  <option value="">Select sport</option>
                                  {sports.map((sport) => (
                                    <option key={sport.id} value={sport.id}>{sport.name}</option>
                                  ))}
                                </SearchableSelect>
                            </td>

                            {/* Event */}
                            <td className="py-3.5 px-3 align-top font-bold text-slate-900">
                              <SearchableSelect
                                value={group.game_id}
                                onChange={(e) => handleGroupChange(group.id, "game_id", e.target.value)}
                                disabled={!group.sport_id}
                                className="w-full min-w-[185px] rounded-lg border border-slate-300 px-2 py-1.5 text-xs bg-white font-normal"
                              >
                                <option value="">Select event</option>
                                {group.game_id &&
                                  group.event &&
                                  !(gamesBySport[String(group.sport_id)] || []).some(
                                    (game) => String(game.id) === String(group.game_id)
                                  ) && (
                                    <option value={group.game_id}>{group.event}</option>
                                  )}
                                {(gamesBySport[String(group.sport_id)] || []).map((game) => (
                                  <option key={game.id} value={game.id}>{game.game_name}</option>
                                ))}
                              </SearchableSelect>
                            </td>

                            {/* All Classes & MQS (Single Row Layout) */}
                            <td className="py-3.5 px-3 align-top">
                              <div className="flex flex-wrap items-center gap-2">
                                {!group.classes.length ? (
                                  <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1 inline-flex items-center gap-1">
                                    <FaInfoCircle className="w-3 h-3" /> No classes assigned yet
                                  </span>
                                ) : (
                                  group.classes.map((c) => {
                                    const hasEquipment =
                                      c.equipment && c.equipment !== "-";

                                    return (
                                      <div
                                        key={c.id}
                                        className="inline-flex items-center gap-1.5 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl px-2.5 py-1 shadow-sm transition group"
                                      >
                                        {/* Class code badge */}
                                        <span className="font-extrabold text-blue-900 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-lg text-xs font-mono">
                                          {c.eligible_classes || c.competition_class}
                                        </span>

                                        {/* MQS display */}
                                        <span className="font-mono text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-150 px-1.5 py-0.5 rounded-md">
                                          MQS: {c.mqs || "None"}
                                        </span>

                                        {/* Equipment badge */}
                                        {hasEquipment && (
                                          <span
                                            className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0.5 rounded font-medium max-w-[100px] truncate"
                                            title={`Equipment: ${c.equipment}`}
                                          >
                                            {c.equipment}
                                          </span>
                                        )}

                                        {/* Edit / Delete actions for each class */}
                                        <div className="flex items-center gap-0.5 ml-1 pl-1 border-l border-slate-200">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openEditClassModal(group.id, c);
                                            }}
                                            title="Edit this class & MQS"
                                            className="p-1 text-slate-400 hover:text-blue-600 rounded transition"
                                          >
                                            <FaEdit className="w-3 h-3" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteClass(group.id, c.id, c.eligible_classes || c.competition_class);
                                            }}
                                            title="Delete this class"
                                            className="p-1 text-slate-400 hover:text-rose-600 rounded transition"
                                          >
                                            <FaTrash className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}

                                {/* + Add Class inline button */}
                                <button
                                  type="button"
                                  onClick={() => openAddClassModal(group.id)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl border border-dashed border-blue-400 text-blue-600 hover:bg-blue-50 text-xs font-semibold transition"
                                  title="Add another class to this event"
                                >
                                  <FaPlus className="w-2.5 h-2.5" />
                                  Add Class
                                </button>
                              </div>
                            </td>

                            {/* Row Actions */}
                            <td className="py-3.5 px-3 text-center align-top">
                              <div className="inline-flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(group.id)}
                                  className={`p-1.5 rounded-lg border text-xs font-semibold transition flex items-center gap-1 ${
                                    isExpanded
                                      ? "bg-slate-200 border-slate-300 text-slate-800"
                                      : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                                  }`}
                                  title={isExpanded ? "Collapse details" : "Expand all class details"}
                                >
                                  {isExpanded ? (
                                    <FaChevronUp className="w-3 h-3" />
                                  ) : (
                                    <FaChevronDown className="w-3 h-3" />
                                  )}
                                  <span className="text-[11px]">{group.classes.length}</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleDeleteGroup(group.id)}
                                  className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                                  title="Delete this event rule and all its classes"
                                >
                                  <FaTrash className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* =========================================================================
                              Expanded Detailed View (Editable Sub-Table)
                             ========================================================================= */}
                          {isExpanded && (
                            <tr className="bg-slate-50/80">
                              <td colSpan={7} className="px-6 py-4 border-y border-slate-200">
                                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                                  <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-3 border-b border-slate-100">
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-slate-800 text-sm">
                                        Class Standards for {group.sport} • {group.event}
                                      </span>
                                      <span className="text-xs text-slate-500 font-medium">
                                        ({group.classes.length} classes defined)
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => openAddClassModal(group.id)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition"
                                    >
                                      <FaPlus className="w-3 h-3" />
                                      Add New Class
                                    </button>
                                  </div>

                                  {!group.classes.length ? (
                                    <p className="text-xs text-slate-400 py-3 text-center">
                                      No classes configured for this event. Click &quot;Add New Class&quot; above to create one.
                                    </p>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-xs">
                                        <thead>
                                          <tr className="text-left text-slate-500 border-b border-slate-100">
                                            <th className="py-2 px-3 w-10">#</th>
                                            <th className="py-2 px-3 w-40">Class</th>
                                            <th className="py-2 px-3 w-36">MQS Standard</th>
                                            <th className="py-2 px-3 w-32">Equipment</th>
                                            <th className="py-2 px-3 w-24 text-center">Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                          {group.classes.map((cls, cIdx) => (
                                            <tr key={cls.id} className="hover:bg-slate-50">
                                              <td className="py-2 px-3 font-mono text-slate-400">
                                                {cIdx + 1}
                                              </td>
                                              <td className="py-2 px-3">
                                                <input
                                                  type="text"
                                                  value={cls.eligible_classes || cls.competition_class}
                                                  onChange={(e) =>
                                                    handleInlineClassChange(
                                                      group.id,
                                                      cls.id,
                                                      "eligible_classes",
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder="e.g. T47"
                                                  className="w-full px-2.5 py-1 border border-slate-300 rounded-lg text-xs font-mono font-bold uppercase text-blue-900 focus:ring-1 focus:ring-blue-500"
                                                />
                                              </td>
                                              <td className="py-2 px-3">
                                                <input
                                                  type="text"
                                                  value={cls.mqs}
                                                  onChange={(e) =>
                                                    handleInlineClassChange(
                                                      group.id,
                                                      cls.id,
                                                      "mqs",
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder="e.g. 00:14.50"
                                                  className="w-full px-2.5 py-1 border border-slate-300 rounded-lg text-xs font-mono font-semibold focus:ring-1 focus:ring-blue-500"
                                                />
                                              </td>
                                              <td className="py-2 px-3">
                                                <input
                                                  type="text"
                                                  value={cls.equipment}
                                                  onChange={(e) =>
                                                    handleInlineClassChange(
                                                      group.id,
                                                      cls.id,
                                                      "equipment",
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder="-"
                                                  className="w-full px-2.5 py-1 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
                                                />
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                <div className="inline-flex items-center gap-1">
                                                  <button
                                                    type="button"
                                                    onClick={() => openEditClassModal(group.id, cls)}
                                                    className="p-1 text-slate-400 hover:text-blue-600 rounded transition"
                                                    title="Edit full class details"
                                                  >
                                                    <FaEdit className="w-3.5 h-3.5" />
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleDeleteClass(
                                                        group.id,
                                                        cls.id,
                                                        cls.eligible_classes || cls.competition_class
                                                      )
                                                    }
                                                    className="p-1 text-slate-400 hover:text-rose-600 rounded transition"
                                                    title="Delete class"
                                                  >
                                                    <FaTrash className="w-3.5 h-3.5" />
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================================
          Class Editor Modal (Add / Edit a Class)
         ========================================================================= */}
      {classModal.isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {classModal.mode === "edit" ? "Edit Class & MQS" : "Add Class to Event"}
                </h3>
                {activeModalGroup && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {activeModalGroup.sport} • {activeModalGroup.event} (
                    {activeModalGroup.gender === "male" ? "Male" : "Female"},{" "}
                    {activeModalGroup.age_category})
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={closeClassModal}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 transition"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Class */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Class <span className="text-rose-500">*</span>
                </label>
                {activeModalGroup ? (
                  <div className="space-y-1.5">
                    <SearchableSelect
                      value={classModal.competitionClass}
                      onChange={(e) => {
                        const val = String(e.target.value || "").toUpperCase();
                        setClassModal((prev) => ({
                          ...prev,
                          competitionClass: val,
                        }));
                      }}
                      className="w-full text-sm rounded-xl border border-slate-300 py-2 px-3"
                    >
                      <option value="">Select or search class</option>
                      {classificationOptionsForSport(activeModalGroup.sport_id).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </SearchableSelect>

                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-400">Or type custom class:</span>
                      <input
                        type="text"
                        value={classModal.competitionClass}
                        onChange={(e) => {
                          const val = String(e.target.value || "").toUpperCase();
                          setClassModal((prev) => ({
                            ...prev,
                            competitionClass: val,
                          }));
                        }}
                        placeholder="e.g. T47, F55, S10"
                        className="flex-1 text-xs border border-slate-300 rounded-lg px-2.5 py-1 uppercase font-mono font-semibold"
                      />
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={classModal.competitionClass}
                    onChange={(e) =>
                      setClassModal((prev) => ({
                        ...prev,
                        competitionClass: e.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="e.g. T47"
                    className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2 font-mono uppercase"
                  />
                )}
              </div>

              {/* Minimum Qualifying Standard (MQS) */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Minimum Qualifying Standard (MQS) <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={classModal.mqs}
                  onChange={(e) => setClassModal((prev) => ({ ...prev, mqs: e.target.value }))}
                  placeholder="e.g. 00:14.50 or 12.50m or 350 pts"
                  className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2 font-mono font-semibold focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  For track/swimming use MM:SS.SS (e.g. 00:14.50); for field use distance (e.g. 12.50m).
                </p>
              </div>

              {/* Equipment */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Equipment / Implement Spec (Optional)
                </label>
                <input
                  type="text"
                  value={classModal.equipment}
                  onChange={(e) => setClassModal((prev) => ({ ...prev, equipment: e.target.value }))}
                  placeholder="e.g. 6kg Shot Put, Throwing Frame, or -"
                  className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={closeClassModal}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-sm font-semibold transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveClassModal}
                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition shadow-sm"
              >
                {classModal.mode === "edit" ? "Save Class" : "Add Class"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          Add Event Rule Modal (Creates a New Event Row)
         ========================================================================= */}
      {eventModal.isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Add New Event Rule</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Create a new single-row event rule for gender, age category, sport, and event.
                </p>
              </div>
              <button
                type="button"
                onClick={closeAddEventModal}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 transition"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {/* Gender */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Gender</label>
                  <SearchableSelect
                    value={eventModal.gender}
                    onChange={(e) => setEventModal((prev) => ({ ...prev, gender: e.target.value }))}
                    className="w-full text-sm rounded-xl border border-slate-300 py-2 px-3"
                  >
                    {GENDER_OPTIONS.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>

                {/* Age Category */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Age Category</label>
                  <SearchableSelect
                    value={eventModal.age_category}
                    onChange={(e) => setEventModal((prev) => ({ ...prev, age_category: e.target.value }))}
                    className="w-full text-sm rounded-xl border border-slate-300 py-2 px-3"
                  >
                    {AGE_OPTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </div>

              {/* Sport */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Sport</label>
                <SearchableSelect
                  value={eventModal.sport_id}
                  onChange={(e) => {
                    const nextSportId = String(e.target.value || "");
                    const firstGame = (gamesBySport[nextSportId] || [])[0]?.id || "";
                    const firstClass = (classificationsBySport[nextSportId] || [])[0] || "";
                    setEventModal((prev) => ({
                      ...prev,
                      sport_id: nextSportId,
                      game_id: String(firstGame),
                      initial_class: firstClass,
                    }));
                  }}
                  className="w-full text-sm rounded-xl border border-slate-300 py-2 px-3"
                >
                  <option value="">Select sport</option>
                  {sports.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              {/* Event / Game */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Event</label>
                <SearchableSelect
                  value={eventModal.game_id}
                  onChange={(e) => setEventModal((prev) => ({ ...prev, game_id: e.target.value }))}
                  disabled={!eventModal.sport_id}
                  className="w-full text-sm rounded-xl border border-slate-300 py-2 px-3"
                >
                  <option value="">
                    {eventModal.sport_id ? "Select event" : "Select sport first"}
                  </option>
                  {(gamesBySport[eventModal.sport_id] || []).map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.game_name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-700 mb-2">Initial Class Standard (Optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">Class</label>
                    <SearchableSelect
                      value={eventModal.initial_class}
                      onChange={(e) => setEventModal((prev) => ({ ...prev, initial_class: e.target.value }))}
                      disabled={!eventModal.sport_id}
                      className="w-full text-xs rounded-lg border border-slate-300 py-1.5 px-2.5"
                    >
                      <option value="">Select class</option>
                      {classificationOptionsForSport(eventModal.sport_id).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">Initial MQS</label>
                    <input
                      type="text"
                      value={eventModal.initial_mqs}
                      onChange={(e) => setEventModal((prev) => ({ ...prev, initial_mqs: e.target.value }))}
                      placeholder="e.g. 00:14.50"
                      className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={closeAddEventModal}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-sm font-semibold transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateEventGroup}
                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition shadow-sm"
              >
                Create Event Rule
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EligibilityManager;
